// OANDA v20 candle source — the founder's own broker feed for XAU/USD.
//
// WHY THIS EXISTS: our earlier gold history came from HistData/Dukascopy
// exports (non-standard clocks, gap months, no live tail). OANDA is the SAME
// feed the studied trader charts on, so his levels line up, and one token
// gives BOTH deep 1-minute history AND the live forming candle. This module
// is the CandleSource the on-disk cache fills from, plus a live-tail fetch for
// the most recent (incomplete) candle.
//
// CREDENTIALS: the personal access token lives ONLY in chrome.storage.local
// (entered in the chart's OANDA panel), never in the repo. The token talks
// only to the user's own OANDA account — exactly the client-side BYO pattern
// the other providers use. Endpoint is the public instrument-candles route,
// which needs the token but no account id.
//
// This is INTERNAL dogfooding tooling (routed only for the XAUUSD family; see
// backtest-provider.ts / registry.ts). It is not a shipped customer feature.

import { storageGet, storageSet, storageRemove } from "../storage.js";
import type { OHLCPoint } from "../market-data.js";
import { candleCache, type RawBar, type MonthBars, type CandleSource } from "./candle-cache.js";

const CREDS_KEY = "providerOandaCreds";
const LIVE_KEY = "oandaLiveTailEnabled";

/** Whether to append OANDA's live forming candle on the latest view. Default
 * on once connected; a toggle in the OANDA panel flips it. */
export async function getOandaLiveEnabled(): Promise<boolean> {
  return await storageGet<boolean>(LIVE_KEY, true);
}
export async function setOandaLiveEnabled(v: boolean): Promise<void> {
  await storageSet(LIVE_KEY, v);
}

export interface OandaCreds {
  token: string;
  accountId?: string;
  /** "practice" (demo, api-fxpractice) or "live" (api-fxtrade). Default practice. */
  env?: "practice" | "live";
}

/** Broker host for the environment. Practice/demo by default. */
export function oandaHost(env: OandaCreds["env"]): string {
  return env === "live" ? "https://api-fxtrade.oanda.com" : "https://api-fxpractice.oanda.com";
}

export async function getOandaCreds(): Promise<OandaCreds | null> {
  const c = await storageGet<OandaCreds | null>(CREDS_KEY, null);
  return c && c.token ? c : null;
}

export async function setOandaCreds(creds: OandaCreds | null): Promise<void> {
  if (creds && creds.token) await storageSet(CREDS_KEY, creds);
  else await storageRemove(CREDS_KEY);
}

// --- pure mappers (unit-tested) ---------------------------------------------

/** Our symbol → OANDA instrument. "XAUUSD" → "XAU_USD"; a date suffix on the
 * backtest symbol ("XAUUSD 2024-08-02") is ignored. Returns null if unmappable. */
export function oandaInstrument(symbol: string): string | null {
  const base = symbol.trim().toUpperCase().split(/\s+/)[0] ?? "";
  const known: Record<string, string> = {
    XAUUSD: "XAU_USD",
    XAGUSD: "XAG_USD",
    EURUSD: "EUR_USD",
    GBPUSD: "GBP_USD",
    USDJPY: "USD_JPY",
  };
  if (known[base]) return known[base]!;
  // Generic 6-letter FX/metal pair → FOO_BAR.
  const m = /^([A-Z]{3})([A-Z]{3})$/.exec(base);
  return m ? `${m[1]}_${m[2]}` : null;
}

/** Our timeframe vocabulary → OANDA granularity. Pure. */
export function oandaGranularity(tf: string): string | null {
  const map: Record<string, string> = {
    "1m": "M1",
    "5m": "M5",
    "15m": "M15",
    "30m": "M30",
    "60m": "H1",
    "1h": "H1",
    "4h": "H4",
    "1d": "D",
    "1w": "W",
    "1wk": "W",
  };
  return map[tf.trim().toLowerCase()] ?? null;
}

interface OandaCandle {
  complete: boolean;
  volume: number;
  time: string; // RFC3339, nanosecond precision
  mid?: { o: string; h: string; l: string; c: string };
  bid?: { o: string; h: string; l: string; c: string };
  ask?: { o: string; h: string; l: string; c: string };
}

/** OANDA candles JSON → our RawBar[]. Prices are strings; time carries
 * nanoseconds we trim to ms. Incomplete (still-forming) candles are dropped
 * unless `includeIncomplete` — history stays complete-only; the live tail asks
 * for the forming bar explicitly. Pure. */
export function parseOandaCandles(json: unknown, includeIncomplete = false): RawBar[] {
  const candles = (json as { candles?: OandaCandle[] })?.candles ?? [];
  const out: RawBar[] = [];
  for (const c of candles) {
    if (!c.complete && !includeIncomplete) continue;
    const px = c.mid ?? c.bid ?? c.ask;
    if (!px) continue;
    // "2024-08-02T13:30:00.000000000Z" → millisecond ISO the cache stores.
    const t = new Date(c.time).toISOString();
    out.push({ t, o: Number(px.o), h: Number(px.h), l: Number(px.l), c: Number(px.c), v: c.volume ?? 0 });
  }
  return out;
}

function monthOf(isoT: string): string {
  const d = new Date(isoT);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** Merge per-timeframe bar series into month payloads for the cache. Pure. */
export function groupBarsByMonth(byTf: Record<string, RawBar[]>): Map<string, MonthBars> {
  const months = new Map<string, MonthBars>();
  const ensure = (m: string): MonthBars => {
    let mb = months.get(m);
    if (!mb) {
      mb = { bars: {} };
      months.set(m, mb);
    }
    return mb;
  };
  for (const [tf, bars] of Object.entries(byTf)) {
    for (const b of bars) {
      const mb = ensure(monthOf(b.t));
      (mb.bars[tf] ??= []).push(b);
    }
  }
  return months;
}

function rfc3339(ms: number): string {
  return new Date(ms).toISOString();
}

/** First-of-month key `n` months before `from` (a YYYY-MM), inclusive floor. */
function isoMonthsBack(nowMs: number, monthsBack: number): number {
  const d = new Date(nowMs);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - monthsBack, 1);
}

// --- network ----------------------------------------------------------------

async function oandaGet(creds: OandaCreds, path: string, params: Record<string, string>): Promise<unknown> {
  const url = new URL(`${oandaHost(creds.env)}/v3${path}`);
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20_000);
  try {
    const res = await fetch(url.toString(), {
      signal: controller.signal,
      headers: { Authorization: `Bearer ${creds.token}`, "Content-Type": "application/json" },
    });
    if (res.status === 401 || res.status === 403) throw new Error("OANDA rejected the token — reconnect in the chart's OANDA panel.");
    if (!res.ok) throw new Error(`OANDA ${path} failed: ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timeout);
  }
}

/** Paged history fetch: OANDA caps a response at 5000 candles, so we walk
 * forward by `from` until we pass `toMs` (or run out). Complete candles only. */
export async function oandaFetchCandles(
  creds: OandaCreds,
  instrument: string,
  granularity: string,
  fromMs: number,
  toMs: number
): Promise<RawBar[]> {
  const all: RawBar[] = [];
  let cursor = fromMs;
  let prevLast = -1;
  let includeFirst = true;
  // Page until the last candle reaches `toMs` or the feed stalls. A SHORT page
  // is NOT the end: OANDA chunks pages around weekend/session gaps and returns
  // <count bars mid-range, so stopping on a short page truncates the month.
  for (let page = 0; page < 2000; page++) {
    const json = await oandaGet(creds, `/instruments/${instrument}/candles`, {
      granularity,
      price: "M",
      from: rfc3339(cursor),
      count: "5000",
      includeFirst: includeFirst ? "true" : "false",
    });
    const bars = parseOandaCandles(json);
    if (!bars.length) break;
    for (const b of bars) {
      if (Date.parse(b.t) > toMs) break;
      all.push(b);
    }
    const pageLastMs = Date.parse(bars[bars.length - 1]!.t);
    if (pageLastMs >= toMs) break; // reached the requested window end
    if (pageLastMs <= prevLast) break; // stall guard: no forward progress
    prevLast = pageLastMs;
    cursor = pageLastMs;
    includeFirst = false;
  }
  return all;
}

/** The live tail: the last few candles INCLUDING the still-forming one, so the
 * chart shows a moving bar. `currentPrice` is that bar's close (latest price). */
export async function oandaLiveCandles(
  creds: OandaCreds,
  instrument: string,
  granularity: string,
  count = 3
): Promise<{ bars: RawBar[]; currentPrice?: number }> {
  const json = await oandaGet(creds, `/instruments/${instrument}/candles`, {
    granularity,
    price: "M",
    count: String(count),
  });
  const bars = parseOandaCandles(json, true); // keep the forming candle
  const last = bars[bars.length - 1];
  return { bars, currentPrice: last?.c };
}

/** The CandleSource the cache fills from on a miss: fetch that month's native
 * 1m/1h/1d from OANDA. `availableMonths` returns empty on purpose — the span
 * is driven by what `backfillGold` has primed into the cache, so a fresh
 * connection doesn't fan a full-history daily open into hundreds of month
 * fetches. Any un-primed month a user scrolls to still fills lazily via
 * `fetchMonth` (which needs no manifest). */
export function oandaSource(creds: OandaCreds): CandleSource {
  return {
    async availableMonths(): Promise<string[]> {
      return [];
    },
    async fetchMonth(symbol, month): Promise<MonthBars | null> {
      const instrument = oandaInstrument(symbol);
      if (!instrument) return null;
      const [y, m] = month.split("-").map(Number);
      const lo = Date.UTC(y!, m! - 1, 1);
      const hi = Date.UTC(y!, m!, 1) - 1; // last ms of the month
      const bars: MonthBars = { bars: {} };
      const [m1, h1, d1] = await Promise.all([
        oandaFetchCandles(creds, instrument, "M1", lo, hi).catch(() => [] as RawBar[]),
        oandaFetchCandles(creds, instrument, "H1", lo, hi).catch(() => [] as RawBar[]),
        oandaFetchCandles(creds, instrument, "D", lo, hi).catch(() => [] as RawBar[]),
      ]);
      if (m1.length) bars.bars["1m"] = m1;
      if (h1.length) bars.bars["1h"] = h1;
      if (d1.length) bars.bars["1d"] = d1;
      return Object.keys(bars.bars).length ? bars : null;
    },
  };
}

export interface BackfillOpts {
  /** Months of 1-minute history to bulk-prime (older 1m fills lazily). */
  minuteMonthsBack?: number;
  /** Months of hourly history to bulk-prime. */
  hourlyMonthsBack?: number;
  /** Full daily history is cheap (one request), so it always loads from 2004. */
}

export interface BackfillSummary {
  months: number;
  minuteBars: number;
  hourlyBars: number;
  dailyBars: number;
}

/** One-shot bulk prime so the first chart open is instant: full daily + a few
 * years of hourly + recent months of 1-minute, written straight to the cache.
 * Anything older/finer fills lazily via `oandaSource`. */
export async function backfillGold(
  creds: OandaCreds,
  symbol: string,
  opts: BackfillOpts = {},
  onProgress?: (msg: string) => void
): Promise<BackfillSummary> {
  const instrument = oandaInstrument(symbol);
  if (!instrument) throw new Error(`OANDA can't map ${symbol}`);
  const now = Date.now();
  const minuteFrom = isoMonthsBack(now, opts.minuteMonthsBack ?? 6);
  const hourlyFrom = isoMonthsBack(now, opts.hourlyMonthsBack ?? 60);
  const dailyFrom = Date.UTC(2004, 0, 1);

  // True replace: drop any earlier-source months so the cache holds OANDA only.
  onProgress?.("Clearing old cache…");
  await candleCache.clearSymbol(symbol);

  onProgress?.("Loading daily history…");
  const daily = await oandaFetchCandles(creds, instrument, "D", dailyFrom, now);
  onProgress?.("Loading hourly history…");
  const hourly = await oandaFetchCandles(creds, instrument, "H1", hourlyFrom, now);
  onProgress?.("Loading 1-minute history…");
  const minute = await oandaFetchCandles(creds, instrument, "M1", minuteFrom, now);

  const months = groupBarsByMonth({ "1d": daily, "1h": hourly, "1m": minute });
  onProgress?.(`Saving ${months.size} months…`);
  for (const [month, payload] of months) await candleCache.putMonth(symbol, month, payload);

  return { months: months.size, minuteBars: minute.length, hourlyBars: hourly.length, dailyBars: daily.length };
}

/** Verify a token by fetching a single candle. Used by the connect UI. */
export async function verifyOandaCreds(creds: OandaCreds): Promise<{ ok: boolean; message?: string }> {
  try {
    const json = await oandaGet(creds, `/instruments/XAU_USD/candles`, { granularity: "D", count: "1", price: "M" });
    const bars = parseOandaCandles(json, true);
    if (bars.length) return { ok: true };
    return { ok: false, message: "Connected, but no XAU/USD candles returned." };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : "Could not reach OANDA." };
  }
}

/** Convenience for surfaces that want an OHLCPoint[] live tail directly. */
export function rawBarsToPoints(bars: RawBar[]): OHLCPoint[] {
  return bars.map((b) => ({ date: new Date(b.t), open: b.o, high: b.h, low: b.l, close: b.c, volume: b.v ?? 0 }));
}
